import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import AvnyFrameImg from "@workspace/main/src/assets/images/Avny Frame.png";

const PreAssessmentPlayBack = ({
  height = "70vh",
  videoSrc,
  botTimestamps,
  chatHistory,
  width = "80%",
  isLoading,
}) => {
  // ========================== Setting ==========================
  const audioPlaybackRate = 1;
  // =============================================================
  const dispatch = useDispatch();
  const videoRef = useRef(null);
  const [audioRefs, setAudioRefs] = useState([]);
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [currentBotAudioIndex, setCurrentBotAudioIndex] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false);
  const [botStatus, setBotStatus] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoTime, setVideoTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoVolume, setVideoVolume] = useState(1);
  const [playbackButton, setPlaybackButton] = useState(true);

  useEffect(() => {
    if (!botTimestamps) {
      return;
    }
    if (botTimestamps.length === 0) {
      return;
    }

    const audios = botTimestamps?.map((item) => {
      if (item.baseEncodedAudio) {
        const audioBlob = new Blob(
          [
            Uint8Array.from(atob(item.baseEncodedAudio), (c) =>
              c.charCodeAt(0)
            ),
          ],
          { type: "audio/mpeg" }
        );
        const audio = new Audio(URL.createObjectURL(audioBlob));
        return audio;
      }
      return null;
    });
    setAudioRefs(audios);
  }, [botTimestamps]);

  useEffect(() => {
    const video = videoRef.current;

    // Event listeners for the video
    const handleLoadedMetadata = () => setVideoDuration(video?.duration || 0);
    const handleVolumeChange = () => {
      const videoVolume = video.volume;
      setVideoVolume(videoVolume);

      // Sync audio volume with video
      if (audioRefs.length > 0) {
        audioRefs.forEach((audio) => {
          if (audio) {
            audio.volume = videoVolume;
          }
        });
      }
    };

    if (video) {
      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      video.addEventListener("volumechange", handleVolumeChange);
    }

    // Cleanup function
    return () => {
      // Stop video playback
      if (video) {
        video.pause();
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
        video.removeEventListener("volumechange", handleVolumeChange);
      }

      // Stop and reset all audio objects
      if (audioRefs.length > 0) {
        audioRefs.forEach((audio) => {
          if (audio) {
            audio.pause();
            audio.currentTime = 0; // Reset playback position to the start
          }
        });
      }
    };
  }, [audioRefs]);

  const mappedMessagesToShow = (messagesToShow) => {
    if (messagesToShow?.length === 0) {
      return null;
    }
    return chatHistory?.slice(0, messagesToShow?.length * 2);
  };

  const updateMessagesAndAudio = async (currentTime) => {
    if (!botTimestamps) {
      return;
    }
    const messagesToShow = botTimestamps?.filter(
      (timestamp) =>
        timestamp.time <= currentTime && timestamp.botStatus === "Speaking..."
    );
    setDisplayedMessages(mappedMessagesToShow(messagesToShow));

    botTimestamps?.forEach(async (timestamp, index) => {
      const audio = audioRefs[index];
      if (
        currentTime >= timestamp.time &&
        currentTime < (botTimestamps[index + 1]?.time || Infinity)
      ) {
        if (audio && videoPlaying) {
          const elapsedTime = (currentTime - timestamp.time) / 1000; // Calculate elapsed time in seconds
          const adjustedTime = elapsedTime / audioPlaybackRate; // Adjust for playback rate

          if (
            audio.paused ||
            Math.abs(audio.currentTime - adjustedTime) > 0.1
          ) {
            // Update currentTime relative to playback rate
            audio.currentTime = adjustedTime;

            try {
              // Set playback rate and play the audio
              await audio.play();
            } catch (error) {
              console.warn("Audio playback interrupted:", error.message);
            }
          }
          setCurrentBotAudioIndex(index);
        }
      } else if (audio && !audio.paused) {
        // Pause other audios not in the current time window
        audio.pause();
      }
    });
  };

  const handleTimeUpdate = () => {
    if (isSeeking) return;
    const video = videoRef.current;
    if (video) {
      const currentTime = video.currentTime * 1000;
      setVideoTime(video.currentTime);
      updateMessagesAndAudio(currentTime);
      const status = generateStatus(currentTime);
      setBotStatus(status);
    }
  };

  const handleSeekStart = () => {
    setIsSeeking(true);
    if (currentBotAudioIndex !== null && audioRefs[currentBotAudioIndex]) {
      audioRefs[currentBotAudioIndex].pause();
    }
  };

  const handleSeekEnd = () => {
    setIsSeeking(false);
    const video = videoRef.current;
    if (video) {
      const currentTime = video.currentTime * 1000;
      updateMessagesAndAudio(currentTime);
      const status = generateStatus(currentTime);
      setBotStatus(status);
    }
  };

  const handlePause = () => {
    setVideoPlaying(false);
    if (currentBotAudioIndex !== null && audioRefs[currentBotAudioIndex]) {
      audioRefs[currentBotAudioIndex].pause();
    }
  };

  const handlePlay = () => {
    setVideoPlaying(true);
    const video = videoRef.current;
    if (video) {
      const currentTime = video.currentTime * 1000;
      updateMessagesAndAudio(currentTime);
      const status = generateStatus(currentTime);
      setBotStatus(status);
    }
  };

  const handleRangeChange = (e) => {
    const video = videoRef.current;
    const newTime = e.target.value;
    setVideoTime(newTime);
    if (video) {
      video.currentTime = newTime;
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      if (videoPlaying) {
        video.play().catch((error) => {
          setPlaybackButton(false);
          console.warn("Video playback interrupted:", error.message);
        });
      } else {
        setPlaybackButton(true);
        video.pause();
      }
    }
  }, [videoPlaying]);

  const togglePlayPause = () => {
    setVideoPlaying(!videoPlaying);
  };

  const handleVolumeChange = (e) => {
    const video = videoRef.current;
    const newVolume = e.target.value;
    setVideoVolume(newVolume);
    if (video) {
      video.volume = newVolume;
    }
  };

  const generateStatus = (currentTime) => {
    const activeStatus = botTimestamps.find(
      (timestamp, index) =>
        currentTime >= timestamp.time &&
        currentTime < (botTimestamps[index + 1]?.time || Infinity)
    );
    return activeStatus ? activeStatus.botStatus : "Waiting...";
  };

  const getVolumeIcon = () => {
    if (videoVolume === 0) {
      return "ri-volume-mute-line";
    } else if (videoVolume < 0.5) {
      return "ri-volume-down-line";
    } else {
      return "ri-volume-up-line";
    }
  };

  return (
    <div>
      {/* // Video and Chatbot */}
      <div className="d-flex flex-column align-items-center">
        <div
          className="flex-grow-1"
          style={{
            borderRadius: "10px",
            padding: "10px",
            border: "1px solid #BED8FF",
            backgroundColor: "#EEF3F8",
            position: "relative",
            // minWidth: width,
          }}
        >
          <img
            src={AvnyFrameImg}
            alt="Avny frame"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "200px",
              height: "auto",
            }}
          />
          <video
            ref={videoRef}
            src={videoSrc}
            controls
            onTimeUpdate={handleTimeUpdate}
            onPause={handlePause}
            onPlay={handlePlay}
            onSeeking={handleSeekStart}
            onSeeked={handleSeekEnd}
            style={{
              objectFit: "fill",
              height: "auto",
              borderRadius: "10px",
              border: "1px solid #CAD329",
            }}
          />
        </div>
      </div>

      {/* Video External Controls 1) Range, Play and pause and Volume */}
      <div
        className="d-flex flex-column justify-content-center mt-5"
        style={{
          width: "100%",
        }}
      >
        <div style={{ position: "relative", minWidth: "100%" }}>
          {/* Current Time on the Left */}
          <div
            style={{
              position: "absolute",
              top: "-20px",
              left: "0",
              fontSize: "14px",
              color: "#333",
            }}
          >
            {`${Math.floor(videoTime / 60)
              .toString()
              .padStart(2, "0")}:${Math.floor(videoTime % 60)
              .toString()
              .padStart(2, "0")}`}
          </div>

          {/* Max Duration on the Right */}
          <div
            style={{
              position: "absolute",
              top: "-20px",
              right: "0",
              fontSize: "14px",
              color: "#333",
            }}
          >
            {`${Math.floor(videoDuration / 60)
              .toString()
              .padStart(2, "0")}:${Math.floor(videoDuration % 60)
              .toString()
              .padStart(2, "0")}`}
          </div>

          {/* Range Input */}
          <input
            type="range"
            min="0"
            max={videoDuration}
            step="0.01"
            value={videoTime}
            onChange={handleRangeChange}
            style={{ width: "100%" }}
          />
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <i className=" ri-settings-2-line" style={{ fontSize: "1.5rem" }} />
          <div className="d-flex align-items-center gap-2">
            <i
              className=" ri-arrow-go-back-line"
              disabled={playbackButton}
              style={{
                fontSize: "1.8rem",
              }}
            ></i>
            {/* <button onClick={togglePlayPause}> */}
            {videoPlaying ? (
              <i
                style={{
                  fontSize: "1.8rem",
                }}
                className="ri-pause-circle-line cursor-pointer"
                onClick={togglePlayPause}
              ></i>
            ) : (
              <i
                style={{
                  fontSize: "1.8rem",
                }}
                className="ri-play-circle-line cursor-pointer"
                onClick={togglePlayPause}
              ></i>
            )}
            {/* </button> */}
            <i
              className=" ri-arrow-go-forward-line"
              disabled={playbackButton}
              style={{
                fontSize: "1.8rem",
              }}
            ></i>
          </div>

          <div className="d-flex gap-2 align-items-center">
            <i
              className={getVolumeIcon()}
              style={{
                fontSize: "1.5rem",
                cursor: "pointer",
              }}
            ></i>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={videoVolume}
              onChange={handleVolumeChange}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreAssessmentPlayBack;
